import type { InitDetail } from 'lightgallery/lg-events';
import type { ComponentProps } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { LightGallery } from '#components/base/LightGallery/LightGallery';
import { notNullOrUndefined } from '#cutils/misc/notNullOrUndefined';

import styles from './ArticleModuleGallery.module.scss';
import sectionStyles from './SectionStyles.module.scss';
import type { ArticleModuleGalleryFragment } from './__generated__/ArticleModuleGallery.generated';

type Props = {
	galleryModule: ArticleModuleGalleryFragment;
};

type ModuleImage = Omit<NonNullable<NonNullable<ArticleModuleGalleryFragment['gallery']>[number]>, 'url'> & { url: string };

type LightGalleryInstance = Parameters<NonNullable<ComponentProps<typeof LightGallery>['onInit']>>[0]['instance'];

function getImageUrlForWidth(baseUrl: URL, width: number) {
	const url = new URL(baseUrl);

	url.searchParams.set('w', width.toString());
	return url.toString();
}

const IMAGE_WIDTHS = [320, 375, 480, 640, 768, 820, 1024, 1194];

/**
 * Creates a gallery image object based on the provided module.
 *
 * @param image - The module from which to create the gallery image.
 * @return  The gallery image object, or null if the module is null or the url is not a string.
 */
function createGalleryImage(image: ModuleImage) {
	const baseUrl = new URL(image.url);

	return {
		src: getImageUrlForWidth(baseUrl, 1366),
		alt: image.altText ?? undefined,
		srcset: IMAGE_WIDTHS.map((width) => {
			return `${getImageUrlForWidth(baseUrl, width)} ${width}w`;
		}).join(','),
		subHtml: `<div class="lg-captions">
                <p>${image.title}</p>
                <p>Bildrechte: ${image.copyright}</p>
            </div>`,
	};
}

export function ArticleModuleGallery({ galleryModule }: Props) {
	const lightGalleryRef = useRef<LightGalleryInstance>(undefined);
	const maximizedStateRef = useRef(false);
	const [galleryContainer, setGalleryContainer] = useState<HTMLDivElement | null>(null);

	const onInit = useCallback(
		(detail: InitDetail) => {
			if (detail && galleryContainer) {
				lightGalleryRef.current = detail.instance;
				lightGalleryRef.current.openGallery();
			}
		},
		[galleryContainer]
	);

	const onContainerResize = useCallback(() => {
		if (lightGalleryRef.current) {
			// if inline is not present the gallery is in maximized mode
			const isMaximized = !lightGalleryRef.current.$container.hasClass('lg-inline');

			if (maximizedStateRef.current !== isMaximized) {
				// add the .lg-on class to the html element
				// that will hide the header when the lightgallery is in maximized mode
				if (isMaximized) {
					document.documentElement.classList.add('lg-on');
				} else {
					document.documentElement.classList.remove('lg-on');
				}

				maximizedStateRef.current = isMaximized;
			}
		}
	}, []);

	const containerRefSetter = useCallback((container: HTMLDivElement) => {
		setGalleryContainer(container);
	}, []);

	const images = useMemo(() => {
		if (!galleryModule.gallery) {
			return null;
		}

		return galleryModule.gallery
			.filter((image): image is ModuleImage => {
				return notNullOrUndefined(image) && typeof image.url === 'string';
			})
			.map((image) => {
				return createGalleryImage(image);
			});
	}, [galleryModule]);

	if (!images) {
		return null;
	}

	return (
		<section className={`${styles.wrapper} ${sectionStyles.section}`} data-theme-area="dark">
			<div className={styles.gallery} ref={containerRefSetter} />
			<LightGallery
				container={galleryContainer ?? ''}
				dynamic={true}
				dynamicEl={images}
				closable={false}
				showMaximizeIcon={true}
				onInit={onInit}
				onContainerResize={onContainerResize}
				download={false}
				resetScrollPosition={false}
			/>
		</section>
	);
}
